import React, { useEffect, useState } from 'react';
import { Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { PasswordChecker } from '../passwordChecker';
import {
  CognitoIdentityProviderClient,
  ConfirmSignUpCommand,
  InvalidParameterException,
  ResendConfirmationCodeCommand,
  SignUpCommand,
  UsernameExistsException,
  UserLambdaValidationException
} from '@aws-sdk/client-cognito-identity-provider';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal } from '@cloudscape-design/components';
import {
  primaryButtonGreen,
  primaryGreenDarker
} from '../../tokenStyle/tokeReset';
import { verifyEmailHash } from './helpers';
import { emailValid } from '../../../helpers/emailHelpers';
import { TermsView } from './termsView';

/**
 * Drop-in Signup Page built with Tailwind CSS
 * -------------------------------------------
 * - Email + Password fields
 * - Show/Hide password toggle
 * - Minimal client-side validation (HTML5)
 * - Accessible labels, focus states, and error text placeholders
 * - Contemporary card-on-gradient design with subtle motion
 *
 * Usage:
 *   export default function App() {
 *     return <SignupPage onSubmit={(data) => console.log(data)} />
 *   }
 *
 * Props:
 *   onSubmit?: (form: { email: string; password: string }) => Promise<void> | void
 */
export default function SignupPageStart({
  onSubmit
}: {
  onSubmit?: (form: {
    email: string;
    password: string;
  }) => Promise<void> | void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [code, setCode] = useState(''); // this is the actual verification code
  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verifyCode, setVerifyCode] = useState<boolean>(false);
  const [resendVerifyCode, setResendVErifyCode] = useState<boolean>(false);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [queryParameters] = useSearchParams();
  const navigate = useNavigate();

  const validatePassword = (pwd: string) => {
    const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
    return regex.test(pwd);
  };
  useEffect(() => {
    const verify = queryParameters.get('verify');
    const vCode = queryParameters.get('code');
    if (verify) {
      setCode(vCode!);
      setVerifyCode(true);
      setInviteCode('123');
    }
    if (vCode) {
      setCode(vCode);
    }
    const inviteCode = queryParameters.get('inviteCode');
    if (inviteCode) {
      setInviteCode(inviteCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (!verifyCode) {
        if (!validatePassword(password)) {
          setError(
            'Password must be at least 8 characters long and include one uppercase letter, one number, and one special character.'
          );
          setLoading(false);
          return;
        }
        createAccount();
      }

      if (verifyCode && code && email) {
        handleVerifyCode();
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const createAccount = async () => {
    try {
      setLoading(true);
      const client = new CognitoIdentityProviderClient({
        region: process.env.REACT_APP_REGION
      });
      let command;
      if (inviteCode) {
        command = new SignUpCommand({
          ClientId: process.env.REACT_APP_COGNITO_CLIENTID,
          Username: email.toLowerCase(),
          Password: password,
          ClientMetadata: {
            inviteCode
          },

          UserAttributes: [
            {
              Name: 'email',
              Value: email
            },
            {
              Name: 'custom:freeTrialKey',
              Value: inviteCode
            }
          ]
        });
      } else {
        command = new SignUpCommand({
          ClientId: process.env.REACT_APP_COGNITO_CLIENTID,
          Username: email.toLowerCase(),
          Password: password,
          ClientMetadata: {
            inviteCode
          },

          UserAttributes: [
            {
              Name: 'email',
              Value: email
            }
          ]
        });
      }
      const response = await client.send(command);
      if (response.$metadata.httpStatusCode === 200) {
        setVerifyCode(true);
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
      if (e instanceof UsernameExistsException) {
        setError('This email address is already associated with an account.');
      } else if (e instanceof InvalidParameterException) {
        setError('The password does not conform to requirements.');
      } else if (e instanceof UserLambdaValidationException) {
        setError('Self-signup is disabled. Valid invite required.');
      } else {
        console.log('error', e);
        setError('Unknow Error');
      }
    }
  };
  const handleVerifyCode = async () => {
    if (!code || !email) {
      setError('Both Email and Code are required');
      return;
    }
    setVerifyLoading(true);
    try {
      const client = new CognitoIdentityProviderClient({
        region: process.env.REACT_APP_REGION
      });
      const cmd = new ConfirmSignUpCommand({
        ClientId: process.env.REACT_APP_COGNITO_CLIENTID,
        Username: email.toLowerCase(),
        ConfirmationCode: code
      });
      const result = await client.send(cmd);
      if (result.$metadata.httpStatusCode === 200) {
        navigate(`/login`);
      }
      setVerifyLoading(false);
    } catch (e) {
      console.log('verify error', JSON.stringify(e));
      setVerifyLoading(false);
      setError('The code may be invalid or expired.');
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      setError('Email Required');
      return;
    }
    try {
      const client = new CognitoIdentityProviderClient({
        region: process.env.REACT_APP_REGION
      });
      const cmd = new ResendConfirmationCodeCommand({
        ClientId: process.env.REACT_APP_COGNITO_CLIENTID,
        Username: email
      });
      const result = await client.send(cmd);
      if (result.$metadata.httpStatusCode === 200) {
        setStatusMessage(
          'If the email is associated with an account, a confirmation code will be sent.'
        );
      }
    } catch (e) {
      console.log('resend Error', JSON.stringify(e));
    }
  };

  useEffect(() => {
    if (!emailValid(email)) {
      setError('');
      return;
    }
    let isActive = true;
    const secret = process.env.REACT_APP_HASH ?? '';

    if (!email || !inviteCode || !secret) {
      return () => {
        isActive = false;
      };
    }

    void verifyEmailHash(email, inviteCode, secret)
      .then((matches) => {
        if (isActive && !matches) {
          setError('Email does not match invite code');
        }
        if (isActive && matches) {
          setError('');
        }
      })
      .catch((err) => {
        console.error('Failed to verify invite hash', err);
      });

    return () => {
      isActive = false;
    };
  }, [email, inviteCode]);
  const inputStyle = `block w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 pl-10 pr-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none focus-visible:ring-2 focus-visible:ring-[${primaryGreenDarker}] focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 transition`;

  return (
    <div className="min-h-screen w-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#62d29e]/40 via-[#62d29e]/20 to-white dark:from-slate-950 dark:via-slate-950 dark:to-black flex items-center justify-center p-6">
      {/* Subtle grid overlay */}
      <div className="pointer-events-none fixed inset-0 opacity-[0.04] [background-image:linear-gradient(to_right,theme(colors.slate.500/.35)_1px,transparent_1px),linear-gradient(to_bottom,theme(colors.slate.500/.35)_1px,transparent_1px)] [background-size:32px_32px]" />

      {inviteCode ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="relative z-10 w-full max-w-md"
        >
          <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-white/80 dark:bg-slate-900/70 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.4)]">
            {/* Header */}
            <div className="px-8 pt-8 pb-4 text-center">
              <img
                src="/korulogo.png"
                alt="Koru Logo"
                className="mx-auto mb-4 h-12 w-12 object-contain"
                loading="eager"
                decoding="async"
              />
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
                {!verifyCode ? 'Create your account' : 'Verify Code'}
              </h1>
              {/* <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              Start your 14-day free trial. No credit card required.
            </p> */}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-8 pb-8 pt-4 space-y-5">
              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                >
                  Email
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <Mail
                      className="h-4 w-4 text-slate-400"
                      aria-hidden="true"
                    />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value.toLowerCase())}
                    placeholder="you@company.com"
                    className={inputStyle}
                  />
                </div>
              </div>

              {/* Password */}
              {!verifyCode && (
                <div>
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="password"
                      className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                    >
                      Password
                    </label>
                    {/* <span className="text-xs text-slate-500 dark:text-slate-400">
                  Min 8 characters
                </span> */}
                  </div>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Lock
                        className="h-4 w-4 text-slate-400"
                        aria-hidden="true"
                      />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPwd ? 'text' : 'password'}
                      minLength={8}
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className={inputStyle}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((s) => !s)}
                      aria-label={showPwd ? 'Hide password' : 'Show password'}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                    >
                      {showPwd ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {password.length > 0 && !validatePassword(password) ? (
                    <PasswordChecker value={password} />
                  ) : null}
                </div>
              )}
              {verifyCode && (
                <div>
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="password"
                      className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                    >
                      Verification Code
                    </label>
                    {/* <span className="text-xs text-slate-500 dark:text-slate-400">
                  Min 8 characters
                </span> */}
                  </div>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Lock
                        className="h-4 w-4 text-slate-400"
                        aria-hidden="true"
                      />
                    </div>
                    <input
                      id="code"
                      name="code"
                      type={'text'}
                      required
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="••••••••"
                      className="block w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 pl-10 pr-10 py-2.5 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 transition"
                    />
                  </div>
                </div>
              )}

              {/* Submit */}
              {!verifyCode && (
                <button
                  type="submit"
                  disabled={loading || !validatePassword(password) || !email}
                  className={`group relative inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[${primaryButtonGreen}] px-4 py-2.5 text-white transition hover:bg-[${primaryGreenDarker}] disabled:opacity-70 disabled:cursor-not-allowed disabled:bg-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-900 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900`}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Creating account…
                    </>
                  ) : (
                    <>Create account</>
                  )}
                </button>
              )}
              {verifyCode && (
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-white transition hover:bg-slate-800 disabled:opacity-70 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-900 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900"
                >
                  {verifyLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>Verify</>
                  )}
                </button>
              )}

              {/* Error placeholder */}
              {error && (
                <>
                  <p
                    className="text-sm text-red-600 dark:text-red-400"
                    role="alert"
                  >
                    {error}
                  </p>
                  {error ===
                    'Self-signup is disabled. Valid invite required.' && (
                    <a href="https://korucompliance.ai" className="text-sm">
                      Learn More
                    </a>
                  )}
                </>
              )}
              {statusMessage && (
                <p className="text-sm" role="alert">
                  {statusMessage}
                </p>
              )}
              {resendVerifyCode && (
                <button
                  onClick={() => handleResendCode()}
                  className="group relative inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-white transition hover:bg-slate-800 disabled:opacity-70 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-900 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900"
                >
                  Resend Code
                </button>
              )}

              {/* Fine print */}
              <div className="text-center text-xs text-slate-500 dark:text-slate-400">
                {/* By creating an account, you agree to our{' '} */}
                <button onClick={() => setShowTerms(true)}>
                  Terms
                </button> and{' '}
                <button onClick={() => setShowPrivacy(true)}>
                  Privacy Policy
                </button>
                .
              </div>
              {/* {!verifyCode && (
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 accent-teal-600
           focus:outline-none focus:ring-2 focus:ring-teal-500
           disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <span className="text-sm text-gray-900 dark:text-white">
                    I have read the Terms and Conditions and Privacy Notice
                  </span>
                </label>
              )} */}

              <div className="text-center text-sm text-slate-600 dark:text-slate-300">
                Already have an account?{' '}
                <a
                  href={`${process.env.REACT_APP_BASE_URL}/login`}
                  className="font-medium text-slate-900 dark:text-white underline-offset-4 hover:underline"
                >
                  Sign in
                </a>
              </div>
            </form>
          </div>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="relative z-10 w-full max-w-md"
        >
          <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-white/80 dark:bg-slate-900/70 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.4)]">
            {/* Header */}
            <div className="px-8 pt-8 pb-4 text-center">
              <img
                src="/korulogo.png"
                alt="Koru Logo"
                className="mx-auto mb-4 h-12 w-12 object-contain"
                loading="eager"
                decoding="async"
              />
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
                Koru is currently Invite only
              </h1>
              {/* <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              Start your 14-day free trial. No credit card required.
            </p> */}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-8 pb-8 pt-4 space-y-5">
              {/* invite code */}
              <div>
                <label
                  htmlFor="email"
                  className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                >
                  Invite Code
                </label>
                <div className="relative">
                  <input
                    id="inviteCode"
                    name="inviteCode"
                    type="text"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    className={inputStyle}
                  />
                </div>
              </div>
              <div className="flex flex-col items-center">
                <p className="text-sm p-4">If you don't have a invite code</p>
                {/* TODO  */}
                <button
                  className={`group relative inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[${primaryButtonGreen}] px-4 py-2.5 text-white transition hover:bg-[${primaryGreenDarker}] disabled:opacity-70 disabled:cursor-not-allowed disabled:bg-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-900 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900`}
                  onClick={() =>
                    (window.location.href =
                      'https://d2c99vdyskp728.cloudfront.net/contact')
                  }
                >
                  Contact Us
                </button>
              </div>

              <div className="text-center text-sm text-slate-600 dark:text-slate-300">
                Already have an account?{' '}
                <a
                  href={`${process.env.REACT_APP_BASE_URL}/login`}
                  className="font-medium text-slate-900 dark:text-white underline-offset-4 hover:underline"
                >
                  Sign in
                </a>
              </div>
            </form>
          </div>
        </motion.div>
      )}
      <TermsView
        showPrivacy={showPrivacy}
        showTerms={showTerms}
        setShowPrivacy={setShowPrivacy}
        setShowTerms={setShowTerms}
      />
    </div>
  );
}
